import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Layers,
} from 'lucide-react';

import { useMenuSecBarQuery } from '@/client/api/menus/menus.query';
import {
  useAddSecBarMemberMutation,
  useRemoveSecBarMemberMutation,
  useReorderSecBarMutation,
} from '@/client/api/menus/menus.mutation';
import { MENU_SEC_PILLARS, MenuSecHierarchyNode } from '@/client/types/menus';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const nodeLabel = (n: MenuSecHierarchyNode) =>
  n.name_menu || n.name_friendly || n.name || `Entity #${n.etid}`;

/** Truthy menu_sec across bool / 0-1 / string forms the API may return. */
const isSecFlag = (n: MenuSecHierarchyNode) => {
  const v = n.menu_sec as unknown;
  return v === true || v === 1 || v === '1' || v === 'true';
};

const getL1Children = (hierarchy?: MenuSecHierarchyNode | null): MenuSecHierarchyNode[] =>
  hierarchy?.children ?? [];

const sortL1 = (nodes: MenuSecHierarchyNode[]) =>
  [...nodes].sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0));

export const SecBarPanel = () => {
  const [pillar, setPillar] = useState(MENU_SEC_PILLARS[0].id);
  const [showHidden, setShowHidden] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Public UI uses sec_menu_active=true — those L1s ARE the blue bar.
  const barQuery = useMenuSecBarQuery(pillar, { sec_menu_active: true });
  // Full pillar tree (includes categories not in the bar) for show/hide editing.
  const allQuery = useMenuSecBarQuery(pillar, { sec_menu_active: false });

  const barL1 = useMemo(
    () => sortL1(getL1Children(barQuery.data?.hierarchy)),
    [barQuery.data?.hierarchy],
  );
  const allL1 = useMemo(
    () => sortL1(getL1Children(allQuery.data?.hierarchy)),
    [allQuery.data?.hierarchy],
  );

  // Authoritative "in bar" set = what the public hierarchy returns (plus menu_sec flag when present).
  const barEtids = useMemo(() => {
    const ids = new Set(barL1.map((n) => n.etid));
    for (const n of allL1) {
      if (isSecFlag(n)) ids.add(n.etid);
    }
    return ids;
  }, [barL1, allL1]);

  const isInBar = (n: MenuSecHierarchyNode) => barEtids.has(n.etid) || isSecFlag(n);

  const rows = showHidden ? allL1 : barL1;
  const visibleForReorder = useMemo(
    () => (allL1.length ? allL1 : barL1).filter((n) => barEtids.has(n.etid) || isSecFlag(n)),
    [allL1, barL1, barEtids],
  );

  const etidRoot = barQuery.data?.etid_root ?? allQuery.data?.etid_root;
  const isLoading = barQuery.isLoading || allQuery.isLoading;
  const isError = barQuery.isError && allQuery.isError;
  const error = barQuery.error ?? allQuery.error;

  const addMutation = useAddSecBarMemberMutation();
  const removeMutation = useRemoveSecBarMemberMutation();
  const reorderMutation = useReorderSecBarMutation();

  const toggleExpand = (etid: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(etid)) next.delete(etid);
      else next.add(etid);
      return next;
    });
  };

  const handleToggleVisibility = async (node: MenuSecHierarchyNode) => {
    const visible = isInBar(node);
    try {
      if (visible) {
        await removeMutation.mutateAsync({ pillar, etid: node.etid });
        toast.success(`Hidden “${nodeLabel(node)}” from bar`);
      } else {
        await addMutation.mutateAsync({ pillar, etid: node.etid });
        toast.success(`Showing “${nodeLabel(node)}” in bar`);
      }
    } catch (e: any) {
      toast.error(e?.message ?? 'Toggle failed');
    }
  };

  const swapOrder = async (indexInVisible: number, dir: -1 | 1) => {
    const other = indexInVisible + dir;
    if (other < 0 || other >= visibleForReorder.length) return;

    const ok = window.confirm(
      'Reordering writes the shared entity_type.order_by and can affect other menus. Continue?',
    );
    if (!ok) return;

    const next = [...visibleForReorder];
    const tmp = next[indexInVisible];
    next[indexInVisible] = next[other];
    next[other] = tmp;
    const order = next.map((n, i) => ({ etid: n.etid, order: (i + 1) * 10 }));

    try {
      const res = await reorderMutation.mutateAsync({ pillar, order });
      if (res?.warning) toast.warning(res.warning);
      else toast.success('Bar order updated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Reorder failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-4 w-4 text-gray-500" />
          <h2 className="text-sm font-medium text-gray-800">Header category bar + mega menu</h2>
        </div>
        <p className="mb-1 text-xs text-gray-500">
          Matches the blue bar under <strong>Market · Homes · Locals</strong>. Pick a pillar, then
          show/hide categories in that bar and reorder them. Expanding a row previews mega-menu
          subtypes (from the entity-type tree — rename those elsewhere).
        </p>
        <p className="mb-4 text-[11px] text-gray-400">
          The three pillar labels themselves are fixed in the public app — not editable here.
        </p>

        <div className="flex flex-wrap gap-2">
          {MENU_SEC_PILLARS.map((p) => {
            const active = p.id === pillar;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPillar(p.id)}
                className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  active
                    ? 'border-blue-200 bg-blue-50 text-blue-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-1.5 font-medium">
                  {p.label}
                  {!p.inPrimaryNav && (
                    <span className="rounded bg-gray-100 px-1 py-0.5 text-[9px] font-normal uppercase text-gray-500">
                      not in nav
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">{p.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-4 py-2.5">
          <div className="text-sm font-medium text-gray-800">
            {MENU_SEC_PILLARS.find((p) => p.id === pillar)?.label ?? pillar}
            {etidRoot != null && etidRoot > 0 && (
              <span className="ml-2 font-mono text-[11px] font-normal text-gray-400">
                root #{etidRoot}
              </span>
            )}
            <span className="ml-2 text-[11px] font-normal text-gray-400">
              {barL1.length} in bar
              {allL1.length > barL1.length ? ` · ${allL1.length} in pillar tree` : ''}
            </span>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show categories not in the blue bar
          </label>
        </div>

        <div className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            “In bar” matches the public site (<code>sec_menu_active=true</code>). Reorder writes
            shared <code>order_by</code>. Categories must belong to this pillar&apos;s tree.
          </span>
        </div>

        {isLoading && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {!isLoading && isError && (
          <div className="px-6 py-10 text-center text-sm text-red-600">
            {(error as Error)?.message ?? 'Failed to load hierarchy'}
          </div>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <div className="px-6 py-14 text-center text-sm text-gray-500">
            {allL1.length === 0
              ? 'No categories under this pillar.'
              : 'No categories currently in the blue bar. Enable “Show categories not in the blue bar” to add some.'}
          </div>
        )}

        {!isLoading &&
          !isError &&
          rows.map((node) => {
            const visible = isInBar(node);
            const reorderIndex = visibleForReorder.findIndex((n) => n.etid === node.etid);
            const kids = node.children ?? [];
            const menuBridge = Array.isArray(node.menu) ? node.menu : [];
            const hasKids = kids.length > 0 || menuBridge.length > 0;
            const isOpen = expanded.has(node.etid);

            return (
              <div key={node.etid}>
                <div
                  className={`group flex items-center gap-2 border-b border-gray-50 px-4 py-2.5 hover:bg-gray-50/80 ${
                    !visible ? 'opacity-55' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => hasKids && toggleExpand(node.etid)}
                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-400 ${
                      hasKids ? 'hover:bg-gray-200 hover:text-gray-700' : 'invisible'
                    }`}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{nodeLabel(node)}</span>
                      <span className="font-mono text-[10px] text-gray-400">etid #{node.etid}</span>
                      {visible ? (
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                          in bar
                        </span>
                      ) : (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                          hidden
                        </span>
                      )}
                      {node.menu_sec_menid != null && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                          menu bridge #{node.menu_sec_menid}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      order {node.order_by ?? '—'}
                      {hasKids
                        ? ` · ${kids.length || menuBridge.length} ${kids.length ? 'subtypes' : 'menu nodes'}`
                        : ''}
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5">
                    {visible && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          disabled={reorderIndex <= 0 || reorderMutation.isPending}
                          onClick={() => swapOrder(reorderIndex, -1)}
                          title="Move up in bar"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          disabled={
                            reorderIndex < 0 ||
                            reorderIndex >= visibleForReorder.length - 1 ||
                            reorderMutation.isPending
                          }
                          onClick={() => swapOrder(reorderIndex, 1)}
                          title="Move down in bar"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-7 gap-1 px-2 text-xs ${
                        visible ? 'text-amber-700' : 'text-emerald-700'
                      }`}
                      disabled={addMutation.isPending || removeMutation.isPending}
                      onClick={() => handleToggleVisibility(node)}
                      title={visible ? 'Hide from bar' : 'Show in bar'}
                    >
                      {visible ? (
                        <>
                          <EyeOff className="h-3.5 w-3.5" /> Hide
                        </>
                      ) : (
                        <>
                          <Eye className="h-3.5 w-3.5" /> Show
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {isOpen && hasKids && (
                  <div className="border-b border-gray-50 bg-slate-50/60">
                    {kids.map((child) => (
                      <div
                        key={child.etid}
                        className="flex items-center gap-2 px-4 py-1.5 pl-14 text-sm text-gray-600"
                      >
                        <span className="min-w-0 flex-1 truncate">{nodeLabel(child)}</span>
                        <span className="font-mono text-[10px] text-gray-400">#{child.etid}</span>
                      </div>
                    ))}
                    {menuBridge.map((m, i) => (
                      <div
                        key={m.menid ?? i}
                        className="flex items-center gap-2 px-4 py-1.5 pl-14 text-sm text-gray-600"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {m.name ?? `Menu #${m.menid}`}
                        </span>
                        <span className="font-mono text-[10px] text-gray-400">
                          menid #{m.menid}
                        </span>
                      </div>
                    ))}
                    <p className="px-4 pb-2 pl-14 text-[10px] text-gray-400">
                      L2 subtypes / menu bridge — edit names &amp; tree in entity-type admin
                    </p>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};
